<template>
  <div class="phone-wrapper">
    <img src="@/assets/img/iframe.png" v-if="showImage" />
    <iframe
      v-if="visible"
      :src="url"
      frameborder="0"
      id="iframe"
      ref="iframeRef"
      sandbox="allow-scripts allow-same-origin allow-forms"
    ></iframe>
  </div>
</template>

<script setup>
import { useEventBus } from '@/utils/useUtils.js'
import { debounce } from 'lodash'
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { getDefaultConfig } from './preview.js'

const $eventBus = useEventBus()
const iframeRef = ref(null)

defineProps({
  visible: {
    type: Boolean,
    default: false,
  },
})


const currentCity = ref({
  headerTitle: '',
  cityCode: '',
  cityName: '',
  firstLetter: '',
  productBasicInfo: [
    {
      tenantId: '',
      cheWuTenantId: '',
      goodsGroupId: '',
      businessIds: [],
      brand: '',
      brandId: '',
      seriesId: '',
      carSeries: '',
      carType: '',
      productName: '',
      nameFirstLetter: 'K',
      qualificationInfo: '',
      qualificationInfoName: '',
      cityCode: '',
      topTags: [],
      pictureUrl: '',
      bottomTags: [],
      carManageYears: '',
      rentPlans: [],
    },
  ],
})
const host = computed(() => {
  const currentHost = window.location.host
  const hostMap = {
    'che-test.cheweijia.com': 'https://lowcode-daily2.cheweijia.com',
    'che-test2.cheweijia.com': 'https://lowcode-daily2.cheweijia.com',
    'che-pre.cheweijia.com': 'https://jiuzhang-pre.cheweijia.com',
    'che.cheweijia.com': 'https://jiuzhang.cheweijia.com',
  }
  return hostMap[currentHost] || 'https://lowcode-daily2.cheweijia.com'
})
const url = computed(() => {
  return `${host.value}/lowcode/previewMobile.html`
})

onBeforeUnmount(() => {
  currentCity.value = {
    headerTitle: '',
    cityCode: '',
    cityName: '',
    firstLetter: '',
    productBasicInfo: [
      {
        tenantId: '',
        cheWuTenantId: '',
        goodsGroupId: '',
        businessIds: [],
        brand: '',
        brandId: '',
        seriesId: '',
        carSeries: '',
        carType: '',
        productName: '',
        nameFirstLetter: 'K',
        qualificationInfo: '',
        qualificationInfoName: '',
        cityCode: '',
        topTags: [],
        pictureUrl: '',
        bottomTags: [],
        carManageYears: '',
        rentPlans: [],
      },
    ],
  }
  $eventBus.$off('iframeDataChange')
  $eventBus.$off('iframePlanChange')
  $eventBus.$off('iframeCompanyChange')
})


// 使用原生 postMessage 发送消息
const postMessageToIframe = debounce((data) => {
  const iframe = iframeRef.value
  if (!iframe?.contentWindow) return

  iframe.contentWindow.postMessage(
    data,
    host.value,
  )
}, 200)

const plans = ref([])
const companyInfo = ref(null)
const carInfo = ref(null)
const emits = defineEmits(['setPreviewLoading'])

const showImage = computed(() => {
  return !(plans.value?.length > 0 || companyInfo.value || carInfo.value)
})

const transferData = async () => {
  transferCarData(carInfo.value)
  transferPlanData(plans.value)
  transferCompanyData(companyInfo.value)
}

watch(
  [() => plans.value, () => companyInfo.value, () => carInfo.value],
  async ([a, b, c]) => {
    if (!a?.length && !b && !c) return
    transferData()
    postMessageToIframe({
      type: 'UPDATE_DATA',
      payload: getDefaultConfig(currentCity.value),
    })
  },
  {
    deep: true,
    immediate: true,
  },
)


// * @property {number} ELECTRIC - 1 纯电动
//  * @property {number} GAS - 2 汽油
//  * @property {number} PLUGINHYBIRDELECT - 3 插电式混动
//  * @property {number} REEV - 4 增程式混动
//  * @property {number} HYBIRDELECT - 5 油电混动
//  * @property {number} DIESEL - 6 柴油
//  * @property {number} GASOLINE - 7 天然气
//  * @property {number} OTHER - 8 其他
// 格式化数据
const transferCarData = (data) => {
  const productName =
    `${data?.brandName || '--'}·${data?.seriesName || ''} ${data?.modelYear || '--'}款` || '暂无'
  const topTags = []
  if (data?.ageName) {
    topTags.push(data?.ageName)
  }
  if (data?.pureElectricDrivingRange && [1, 3, 4].includes(data?.energyType)) {
    topTags.push(`续航${data?.pureElectricDrivingRange}公里`)
  }
  if (data?.fuelConsumption && [2, 5, 6, 7].includes(data?.energyType)) {
    topTags.push(`百公里油耗${data?.fuelConsumption}L`)
  }
  if (data?.carCert && data?.carCertName) {
    topTags.push(data?.carCertName)
  }

  let pictureUrl = ''
  if (data?.carModelPics?.length) {
    pictureUrl = data?.carModelPics?.[0] || ''
  }

  currentCity.value.headerTitle = data?.operationTypeName
  currentCity.value.cityCode = data?.cityCode
  currentCity.value.cityName = data?.cityName || '--'
  currentCity.value.headerTitle = data?.operationTypeName

  const { productBasicInfo } = currentCity.value
  if (!productBasicInfo?.length) return

  productBasicInfo[0] = {
    ...productBasicInfo[0],
    brand: data?.brandName,
    brandId: data?.brandId,
    seriesId: data?.seriesId,
    carSeries: data?.seriesName,
    carType: data?.operationType,
    productName,
    nameFirstLetter: 'K',
    qualificationInfo: data?.carLevel,
    qualificationInfoName: data?.operationType === 10 ? data?.carLevelName : null,
    cityCode: data?.cityCode,
    topTags,
    pictureUrl,
  }
  currentCity.value.productBasicInfo = productBasicInfo
}
const transferPlanData = (data) => {
  const { productBasicInfo } = currentCity.value
  if (!productBasicInfo?.length) return
  const rentPlans = []
  data?.forEach((plan) => {
    const { rentPeriod, rentPeriodType } = plan
    let rentPeriodDesc = ''
    if (rentPeriodType === 1) {
      rentPeriodDesc = `${rentPeriod}天`
    } else {
      rentPeriodDesc = `${rentPeriod}个月`
    }
    rentPlans.push({
      goodsId: plan?.id,
      rentPeriod: plan?.rentPeriod,
      rentPeriodType: plan?.rentPeriodType,
      rentPeriodShowName: rentPeriodDesc,
      presentDays: plan?.presentDays,
      rentAmountUnit: plan?.rentPeriodTypeName,
      rentAmount: plan?.rentAmount?.replace('元', ''),
      formatRentAmount: plan?.formatRentAmount,
      rentAmountDay: plan?.dailyRentAmount,
      depositPayMode: 6,
      deposit: plan?.deposit?.replace('元', ''),
      rentCategory: 1,
      state: plan?.state,
    })
  })

  productBasicInfo[0].rentPlans = [...rentPlans]
  currentCity.value.productBasicInfo = productBasicInfo
}

const transferCompanyData = (data) => {
  if (!data) return
  const { productBasicInfo } = currentCity.value
  if (!productBasicInfo?.length) return
  const bottomTags = []
  const { cooperationPlatformDesc = [], businessYears } = data

  if (cooperationPlatformDesc?.length) {
    cooperationPlatformDesc.forEach((item) => {
      bottomTags.push(item)
    })
  }

  productBasicInfo[0].bottomTags = bottomTags?.filter((item) => item) || []
  productBasicInfo[0].carManageYears = businessYears
  currentCity.value.productBasicInfo = productBasicInfo
}

onMounted(() => {
  const iframe = iframeRef.value
  if (iframe) {
    window.addEventListener('message', (event) => {
      if (event.origin !== host.value) return
      if (event.data && event.data.type === 'IFRAME_READY') {
        emits('setPreviewLoading', true)
        postMessageToIframe({
          type: 'UPDATE_DATA',
          payload: getDefaultConfig(currentCity.value),
        })
      }
    })

    // 添加加载失败处理
    iframe.onerror = () => {
      emits('setPreviewLoading', true)
    }

    // 添加超时处理
    const timeoutId = setTimeout(() => {
      emits('setPreviewLoading', true)
    }, 10000) // 10秒超时

    iframe.onload = () => {
      clearTimeout(timeoutId) // 清除超时定时器

      $eventBus.$on('iframeDataChange', (data) => {
        carInfo.value = { ...data }
      })

      $eventBus.$on('iframePlanChange', (data) => {
        plans.value = [...data] || []
      })

      $eventBus.$on('iframeCompanyChange', (data) => {
        companyInfo.value = { ...data }
      })
    }
  }
})
</script>

<style lang="scss" scoped>
.phone-wrapper {
  width: 375px;
  position: fixed;
  height: 812px;
  transform: scale(0.747);
  top: -55px;
  left: -32px;
  background: #fff;
  border-radius: 16px;
  border: 4px solid #5e5e5e;
  box-sizing: border-box;
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.1);
  overflow: hidden;

  > img {
    width: 100%;
    height: 100%;
  }

  iframe {
    width: 100%;
    height: 100%; // 恢复为全高
    border: none;
  }
}
</style>
